/**
 * TimeSavedChip - "13 h saved" in a detail page's header, opening the full
 * line (R4-15).
 *
 * The feedback: every item should say how long such work USUALLY takes and
 * how long it took in PONS. `TimeSavedLine` says exactly that, with the
 * by-hand figure editable, but it was mounted nowhere. Draft, table and
 * review headers are full rows, so the figure goes there as one compact
 * chip, and the line - with its editor - opens beneath it.
 *
 * It fetches its own figure, and renders nothing until there is one (or when
 * the item has no saving to show - a run still in progress, say).
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { PiClockLight } from 'react-icons/pi';
import TimeSavedLine from './TimeSavedLine';
import {
  TimeSavedItem, TimeSavedItemKind, formatHours, getTimeSavedItem,
} from '../../utils/timeSavedApi';

interface Props {
  kind: TimeSavedItemKind;
  uuid: string;
  className?: string;
}

const TimeSavedChip: React.FC<Props> = ({ kind, uuid, className = '' }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<TimeSavedItem | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    getTimeSavedItem(kind, uuid)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { /* a side figure: no chip rather than an error */ });
    return () => { alive = false; };
  }, [kind, uuid]);

  if (!data || !(data.saved_hours > 0)) return null;

  return (
    <Popover className={`relative ${className}`} data-testid="time-saved-chip">
      <PopoverButton
        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-hairline-soft px-2 text-xs text-fg-body
                   transition-colors hover:bg-surface-sunken/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40"
        title={t('Time saved on this item')}
      >
        <PiClockLight className="h-3.5 w-3.5 text-success-600 dark:text-success-400" aria-hidden />
        {t('{{hours}} saved', { hours: formatHours(data.saved_hours) })}
      </PopoverButton>
      <PopoverPanel
        anchor="bottom end"
        className="z-[300] w-[22rem] max-w-[calc(100vw-16px)] rounded-xl bg-surface p-4 shadow-xl ring-1 ring-black/[0.05]
                   [--anchor-gap:6px] dark:ring-white/[0.08]"
      >
        <TimeSavedLine kind={kind} uuid={uuid} />
      </PopoverPanel>
    </Popover>
  );
};

export default TimeSavedChip;
